import styled, { css } from 'styled-components'

import { Button } from '../../../components/ui'

const stageTone = {
  light: {
    plan: {
      background: '#EDF3FF',
      selected: '#DCE7FA',
      text: '#667BA7',
      selectedText: '#314A7A',
      border: '#D8E2F7',
      selectedBorder: '#7890C2',
      hover: '#E1EAFC',
      selectedHover: '#D2DFF6',
      dot: '#A9B8D8',
    },
    execution: { background: '#FFF0F4', text: '#B84B68', border: '#F4D9E1', hover: '#FBE1E8' },
    record: { background: '#EDF7FC', text: '#47758C', border: '#D7EAF3', hover: '#DFF0F8' },
    review: { background: '#FFF5E8', text: '#926B38', border: '#F0DFC4', hover: '#F9E9D1' },
  },
  dark: {
    plan: {
      background: '#243149',
      selected: '#33486C',
      text: '#9AAFD5',
      selectedText: '#D4DDF2',
      border: '#34445E',
      selectedBorder: '#7890C0',
      hover: '#2D3D5A',
      selectedHover: '#3C5278',
      dot: '#7185AF',
    },
    execution: { background: '#3A2630', text: '#EE91A8', border: '#573444', hover: '#492C39' },
    record: { background: '#20333D', text: '#8FC5DA', border: '#304A56', hover: '#29414C' },
    review: { background: '#3A3023', text: '#E0B476', border: '#554630', hover: '#493B2A' },
  },
}

const getStageTone = (theme, tone = 'plan') => {
  const palette = stageTone[theme.mode] || stageTone.light
  return palette[tone] || palette.plan
}

export const HomeHeader = styled.header`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: clamp(32px, 5vw, 52px);

  @media (max-width: 900px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 30px;
  }
`

export const HeaderCopy = styled.div`
  min-width: 0;

  h1 {
    margin: 0;
    color: ${({ theme }) => theme.colors.text};
    font-size: clamp(1.75rem, 3vw, 2.35rem);
    line-height: 1.2;
    letter-spacing: -0.045em;
  }
  p { margin: 9px 0 0; color: ${({ theme }) => theme.colors.textMuted}; font-size: 0.94rem; }
`

export const TodayLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 7px;

  > span {
    color: ${({ theme }) => theme.colors.progressAccent};
    font-size: 0.76rem;
    font-weight: 760;
    letter-spacing: 0.08em;
  }

  button {
    width: 22px;
    height: 22px;
    flex-basis: 22px;
  }
`

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

export const InlineIconButton = styled.button`
  display: grid;
  width: 30px;
  height: 30px;
  place-items: center;
  padding: 0;
  color: ${({ $muted, theme }) => $muted ? theme.colors.textMuted : theme.colors.primary};
  border: 0;
  border-radius: 6px;
  background: transparent;

  svg { width: 20px; height: 20px; }
  &:hover, &:focus-visible { background: ${({ theme }) => theme.colors.primarySoft}; }
  &:disabled { cursor: not-allowed; opacity: 0.45; }

`

export const SmallButton = styled(Button)`
  min-height: 34px;
  padding: 0 11px;
  font-size: 0.77rem;
`

export const PartialError = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
  padding: 10px 14px;
  color: ${({ theme }) => theme.colors.danger};
  border: 1px solid rgba(196, 61, 75, 0.2);
  border-radius: ${({ theme }) => theme.radius.small};
  background: rgba(196, 61, 75, 0.06);
  font-size: 0.8rem;

  button {
    display: inline-flex;
    min-height: 30px;
    flex: 0 0 auto;
    align-items: center;
    gap: 5px;
    padding: 0 10px;
    color: ${({ theme }) => theme.colors.danger};
    cursor: pointer;
    border: 1px solid rgba(196, 61, 75, 0.24);
    border-radius: 999px;
    background: ${({ theme }) => theme.colors.surface};
    font: inherit;
    font-weight: 680;
    transition: border-color 140ms ease, background 140ms ease, transform 140ms ease;

    svg { font-size: 1rem; }
    &:hover { border-color: rgba(196, 61, 75, 0.48); background: rgba(196, 61, 75, 0.08); }
    &:active { transform: translateY(1px); }
    &:focus-visible { outline: 3px solid rgba(196, 61, 75, 0.18); outline-offset: 2px; }
  }

  @media (max-width: 480px) {
    align-items: flex-start;
    flex-direction: column;
  }
`

export const Journey = styled.div`
  display: grid;
  width: 100%;
  max-width: 1210px;
  margin: 0 auto;
`

export const JourneyStep = styled.section`
  position: relative;
  display: grid;
  grid-template-columns: 50px minmax(0, 1fr);
  gap: 18px;
  align-items: stretch;

  @media (max-width: 640px) {
    display: block;
  }
`

export const StepRail = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  padding-top: 61px;

  &::after {
    position: absolute;
    z-index: 0;
    top: 99px;
    bottom: -20px;
    left: 50%;
    width: 1px;
    background: ${({ theme }) => theme.colors.borderStrong};
    content: '';
    transform: translateX(-50%);
  }

  ${JourneyStep}:last-child &::after { display: none; }

  @media (max-width: 640px) {
    position: absolute;
    z-index: 2;
    top: 16px;
    right: 16px;
    width: 38px;
    padding-top: 0;
    transform: none;

    &::after {
      display: none;
    }
  }
`

export const StepMarker = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  width: 38px;
  height: 38px;
  place-items: center;
  color: ${({ $complete, $active, theme }) => ($complete || $active ? '#FFFFFF' : theme.colors.primaryAccent)};
  border: 2px solid ${({ $complete, $active, theme }) => (
    $complete ? theme.colors.primary : $active ? theme.colors.progressAccent : theme.colors.borderStrong
  )};
  border-radius: 50%;
  background: ${({ $complete, $active, theme }) => (
    $complete ? theme.colors.primary : $active ? theme.colors.progressAccent : theme.colors.surface
  )};
  box-shadow: 0 0 0 5px ${({ theme }) => theme.colors.background};
  font-size: 0.84rem;
  font-weight: 760;

  svg { width: 20px; height: 20px; }

  @media (max-width: 640px) {
    width: 38px;
    height: 38px;
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.surface};
    font-size: 0.86rem;

    svg { width: 22px; height: 22px; }
  }
`

export const JourneyCard = styled.div`
  position: relative;
  z-index: 0;
  min-width: 0;
  margin-left: -72px;
  margin-bottom: 18px;
  overflow: hidden;
  border: ${({ $active }) => $active ? '2px' : '1px'} solid ${({ $active, theme }) => $active ? (theme.mode === 'dark' ? theme.colors.primaryAccent : '#AFC8FF') : theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: none;

  @media (max-width: 640px) {
    margin-left: 0;
    margin-bottom: 14px;
    border-radius: ${({ theme }) => theme.radius.medium};
  }
`

export const CompactCardBody = styled.div`
  display: flex;
  min-height: 160px;
  align-items: center;
  justify-content: space-between;
  gap: 35px;
  padding: 29px clamp(26px, 3.3vw, 37px) 29px calc(clamp(26px, 3.3vw, 37px) + 41px);

  @media (max-width: 900px) {
    min-height: 0;
    align-items: stretch;
    flex-direction: column;
    gap: 22px;
    padding: 24px 22px 24px 63px;
  }

  @media (max-width: 640px) {
    padding-left: 22px;
  }
`

export const CompactCopy = styled.div`
  min-width: 0;
  flex: 1;

  > span {
    display: block;
    margin-bottom: 9px;
    color: ${({ theme }) => theme.colors.progressAccent};
    font-size: 0.7rem;
    font-weight: 760;
  }
  > h2 {
    margin: 0;
    color: ${({ theme }) => theme.colors.text};
    font-size: 1.08rem;
    line-height: 1.4;
    letter-spacing: -0.025em;
    overflow-wrap: anywhere;
  }

  @media (max-width: 640px) {
    > h2 { padding-right: 42px; }
  }
`

export const CompactLoading = styled.div`
  max-width: 420px;
  margin-top: 14px;
`

export const CompactMessage = styled.p`
  margin: 14px 0 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.78rem;
`

export const PlanChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;

  @media (max-width: 420px) {
    gap: 9px;
  }
`

export const PlanChipTop = styled.div`
  display: flex;
  align-items: center;
`

export const PlanChip = styled.div`
  display: inline-flex;
  min-width: ${({ $editing }) => $editing ? '292px' : '0'};
  align-items: stretch;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid ${({ $selected, theme }) => $selected
    ? getStageTone(theme, 'plan').selectedBorder
    : getStageTone(theme, 'plan').border};
  border-radius: 9px;
  background: ${({ $selected, theme }) => $selected
    ? getStageTone(theme, 'plan').selected
    : getStageTone(theme, 'plan').background};
  box-shadow: none;

  > ${PlanChipTop} > button {
    display: inline-flex;
    min-height: 38px;
    align-items: center;
    gap: 10px;
    padding: 0 14px;
    color: ${({ $selected, theme }) => $selected
      ? getStageTone(theme, 'plan').selectedText
      : getStageTone(theme, 'plan').text};
    border: 0;
    background: transparent;
    font-size: 0.72rem;
  }
  > ${PlanChipTop} > button:first-child { min-width: 0; flex: 1; padding-right: 7px; }
  > ${PlanChipTop} > button:last-child { width: 30px; flex: 0 0 30px; justify-content: center; padding: 0 8px 0 0; opacity: 0; transition: opacity 120ms ease; }
  strong {
    color: ${({ $selected, theme }) => $selected
      ? getStageTone(theme, 'plan').selectedText
      : getStageTone(theme, 'plan').text};
    font-size: 0.78rem;
    white-space: nowrap;
  }
  > ${PlanChipTop} svg { width: 13px; height: 13px; opacity: 0.65; }
  > ${PlanChipTop} > button:hover, > ${PlanChipTop} > button:focus-visible {
    color: ${({ theme }) => getStageTone(theme, 'plan').selectedText};
    background: ${({ $selected, theme }) => $selected
      ? getStageTone(theme, 'plan').selectedHover
      : getStageTone(theme, 'plan').hover};
  }
  &:hover > ${PlanChipTop} > button:last-child, &:focus-within > ${PlanChipTop} > button:last-child { opacity: 1; }

  @media (hover: none) {
    > ${PlanChipTop} > button:last-child { opacity: 1; }
  }

  @media (max-width: 420px) {
    width: ${({ $editing }) => $editing ? '100%' : 'auto'};
    max-width: 100%;

    > ${PlanChipTop} > button:first-child { min-width: 0; }
    > ${PlanChipTop} > button:first-child span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  }
`

export const PlanSelection = styled.span`
  width: ${({ $selected }) => $selected ? '9px' : '7px'};
  height: ${({ $selected }) => $selected ? '9px' : '7px'};
  flex: 0 0 ${({ $selected }) => $selected ? '9px' : '7px'};
  border-radius: 50%;
  background: ${({ $selected, theme }) => $selected
    ? (theme.mode === 'dark' ? '#A4B6DD' : '#687BA5')
    : getStageTone(theme, 'plan').dot};
  box-shadow: ${({ $selected, theme }) => $selected
    ? `0 0 0 3px ${theme.mode === 'dark' ? '#344466' : '#DCE5F8'}`
    : 'none'};
  transition: width 120ms ease, height 120ms ease, background 120ms ease, box-shadow 120ms ease;
`

export const CompactCompletionForm = styled.form`
  display: grid;
  width: 100%;
  box-sizing: border-box;
  grid-template-columns: auto 58px auto 30px 30px;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 10px 12px 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surfaceTranslucent};

  label { color: ${({ theme }) => theme.colors.textMuted}; font-size: 0.72rem; white-space: nowrap; }
  input {
    width: 58px;
    height: 30px;
    padding: 4px 7px;
    color: ${({ theme }) => theme.colors.text};
    border: 1px solid ${({ $error, theme }) => $error ? theme.colors.danger : theme.colors.borderStrong};
    border-radius: 6px;
    background: ${({ theme }) => theme.colors.surface};
    font-size: 0.78rem;
  }
  p {
    grid-column: 1 / -1;
    margin: 3px 0 0;
    color: ${({ theme }) => theme.colors.danger};
    font-size: 0.68rem;
    line-height: 1.4;
  }
`

export const CompletionLimit = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.72rem;
  white-space: nowrap;
`

export const MetaChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;

  > span {
    display: inline-flex;
    min-height: 38px;
    align-items: center;
    padding: 0 14px;
    color: ${({ $tone, theme }) => getStageTone(theme, $tone).text};
    border: 1px solid ${({ $tone, theme }) => getStageTone(theme, $tone).border};
    border-radius: 7px;
    background: ${({ $tone, theme }) => getStageTone(theme, $tone).background};
    font-size: 0.72rem;
  }

  @media (max-width: 420px) {
    gap: 8px;
  }
`

export const AttendanceChip = styled.button`
  display: inline-flex;
  min-height: 38px;
  align-items: center;
  gap: 6px;
  padding: 0 14px;
  color: ${({ $complete, theme }) => $complete ? theme.colors.success : getStageTone(theme, 'execution').text};
  border: 1px solid ${({ $complete, theme }) => $complete ? `${theme.colors.success}32` : getStageTone(theme, 'execution').border};
  border-radius: 7px;
  background: ${({ $complete, theme }) => $complete ? `${theme.colors.success}14` : getStageTone(theme, 'execution').background};
  font-size: 0.72rem;
  font-weight: 650;

  svg { width: 20px; height: 20px; flex: 0 0 20px; }
  &:disabled { cursor: wait; opacity: 0.65; }
`

export const CompleteChip = styled.span`
  && {
    gap: 6px;
    color: ${({ theme }) => theme.colors.success};
    border-color: ${({ theme }) => `${theme.colors.success}32`};
    background: ${({ theme }) => `${theme.colors.success}14`};
    font-weight: 650;
  }

  svg { width: 20px; height: 20px; flex: 0 0 20px; }
`

export const ReportTypeChip = styled.span`
  && {
    min-height: 38px;
    padding: 0 17px;
    color: ${({ theme }) => getStageTone(theme, 'review').text};
    border: 1px solid ${({ theme }) => getStageTone(theme, 'review').border};
    border-radius: ${({ theme }) => theme.radius.pill};
    background: ${({ theme }) => getStageTone(theme, 'review').background};
    font-size: 0.76rem;
    font-weight: 680;
  }
`

export const CompactLink = styled.button`
  display: inline-flex;
  min-height: 36px;
  flex: 0 0 auto;
  align-items: center;
  gap: 3px;
  padding: 0;
  color: ${({ theme }) => theme.colors.textMuted};
  border: 0;
  background: transparent;
  font-size: 0.78rem;
  font-weight: 660;
  white-space: nowrap;

  svg { width: 16px; height: 16px; }
  &:hover, &:focus-visible { color: ${({ theme }) => theme.colors.primary}; }

  @media (max-width: 900px) { align-self: flex-end; }
`

export const RecordActions = styled.div`
  display: grid;
  flex: 0 0 auto;
  align-self: stretch;
  grid-template-rows: ${({ $hasRecord }) => $hasRecord ? '1fr 38px' : '1fr'};
  align-items: center;
  justify-content: flex-end;
  gap: 16px;

  > ${CompactLink} {
    min-height: 38px;
    justify-content: flex-end;
  }

  @media (max-width: 900px) {
    display: grid;
    width: 100%;
    align-self: auto;
    grid-template-columns: minmax(0, 1fr);
    gap: 14px;

    > ${CompactLink} { min-height: 42px; justify-self: end; }
  }
`

const compactActionButton = css`
  display: inline-flex;
  width: 260px;
  max-width: 100%;
  min-height: 42px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 18px;
  color: ${({ theme }) => theme.mode === 'dark' ? theme.colors.primaryAccent : theme.colors.primary};
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.radius.small};
  background: ${({ theme }) => theme.mode === 'dark' ? '#26334F' : '#E4EAF6'};
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.4;
  text-align: center;
  white-space: nowrap;

  svg { width: 16px; height: 16px; flex: 0 0 16px; }

  &:hover, &:focus-visible {
    color: ${({ theme }) => theme.mode === 'dark' ? theme.colors.primaryAccent : theme.colors.primary};
    border-color: transparent;
    background: ${({ theme }) => theme.mode === 'dark' ? '#31415F' : '#D5DFF1'};
  }
`

export const RecordAddButton = styled.button`
  ${compactActionButton};

  @media (max-width: 900px) { width: 100%; }
`

export const CompactPrimaryButton = styled.button`
  ${compactActionButton};
  flex: 0 0 auto;

  @media (max-width: 900px) { width: 100%; max-width: none; }
`

export const CompactReviewButton = styled(CompactPrimaryButton)`
  &:disabled { cursor: wait; opacity: 0.6; }
`

export const LogPreview = styled.p`
  max-width: 640px;
  margin: 12px 0 0;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.8rem;
  line-height: 1.45;
  text-overflow: ellipsis;
  white-space: nowrap;
`
